import { TeamResource } from 'dy-frontend-http-repository/lib/modules/Team/resources';
import React, { useEffect, useMemo, useState } from 'react';

import OccupancyRate from '@app/containers/components/OccupancyRate';
import { Callout, Card, Classes, HTMLTable, Intent, Spinner } from '@blueprintjs/core';
import Flex from '@components/Flex';
import DevText from '@components/Text';

import { TeamPeriodOccupancyResource, fetchTeamPeriodOccupancy } from '../../data/api';
import { Controls, TableScroll, Toolbar } from '../../styled';
import {
    buildParticipantScoreMap,
    calculateOverallScore,
    collectMembers,
    filterMembersByTeam,
    formatMonthLabel,
    getUtcMonthPeriod,
    getUtcMonthValue,
    isSelectableMonth,
    sortByScoreDescending,
    toggleTeamSelection,
} from '../../utils/teamHealth';
import MemberIdentity, { getMemberRole } from '../MemberIdentity';
import OccupancyBars from '../OccupancyBars';

export interface MonthlyOccupancyProps {
    teams: TeamResource[];
}

type MonthlyResult =
    | { monthValue: string; items: TeamPeriodOccupancyResource[] }
    | { monthValue: string; error: string };

const MonthlyOccupancy: React.FC<MonthlyOccupancyProps> = ({ teams }) => {
    const currentMonthValue = getUtcMonthValue(new Date());
    const [monthValue, setMonthValue] = useState(currentMonthValue);
    const [result, setResult] = useState<MonthlyResult | null>(null);
    const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);

    useEffect(() => {
        const period = getUtcMonthPeriod(monthValue);
        if (!period) {
            return;
        }

        let isCancelled = false;
        setResult(null);
        fetchTeamPeriodOccupancy(
            teams.map((team) => team.id),
            period
        )
            .then((items) => !isCancelled && setResult({ monthValue, items }))
            .catch((e) => {
                console.error(e);
                if (!isCancelled) {
                    setResult({ monthValue, error: 'Occupancy for this month could not be loaded.' });
                }
            });

        return () => {
            isCancelled = true;
        };
    }, [teams, monthValue]);

    const members = useMemo(() => collectMembers(teams), [teams]);

    // Only render data that answers the month currently selected
    const current = result && result.monthValue === monthValue ? result : null;
    const items = current && 'items' in current ? current.items : null;

    const teamScores = useMemo(() => {
        if (!items) {
            return [];
        }

        const scoreByTeamId: Record<string, number> = {};
        items.forEach((item) => (scoreByTeamId[item.team_id] = item.score));

        return sortByScoreDescending(
            teams
                .filter((team) => team.id in scoreByTeamId)
                .map((team) => ({ id: team.id, title: team.title, score: scoreByTeamId[team.id] }))
        );
    }, [teams, items]);

    const participantScores = useMemo(() => (items ? buildParticipantScoreMap(items) : {}), [items]);

    const renderMonthPicker = () => (
        <Controls>
            <div>
                <label htmlFor="team-health-month">Month</label>
                <input
                    id="team-health-month"
                    type="month"
                    className={Classes.INPUT}
                    value={monthValue}
                    max={currentMonthValue}
                    aria-label={`Month, currently ${formatMonthLabel(monthValue)}`}
                    onChange={(e) => {
                        // Historical months are unlimited; a future or partially typed month is ignored
                        if (isSelectableMonth(e.target.value, new Date())) {
                            setMonthValue(e.target.value);
                            setSelectedTeamId(null);
                        }
                    }}
                />
            </div>
        </Controls>
    );

    const renderTeamPanel = () => {
        if (current && 'error' in current) {
            return <Callout intent={Intent.DANGER}>{current.error}</Callout>;
        }

        if (!items) {
            return (
                <Flex justify="center">
                    <Spinner size={30} />
                </Flex>
            );
        }

        if (teamScores.length === 0) {
            return <DevText muted>No active teams to report on.</DevText>;
        }

        return (
            <OccupancyBars
                overallScore={calculateOverallScore(teamScores.map((team) => team.score))}
                items={teamScores}
                selectedId={selectedTeamId}
                onSelect={(id) => setSelectedTeamId(toggleTeamSelection(selectedTeamId, id))}
            />
        );
    };

    const renderOccupancy = (userId: string) => {
        if (!items) {
            return (
                <DevText inline className={Classes.SKELETON}>
                    0.00%
                </DevText>
            );
        }

        const score = participantScores[userId];
        if (score === undefined) {
            return (
                <DevText inline muted>
                    Not scored
                </DevText>
            );
        }

        return <OccupancyRate colorByBand score={score} style={{ minWidth: '160px' }} />;
    };

    const visibleMembers = filterMembersByTeam(members, selectedTeamId);

    return (
        <>
            <Toolbar>
                <DevText muted>{formatMonthLabel(monthValue)} · weekdays through today (UTC)</DevText>
                {renderMonthPicker()}
            </Toolbar>

            <Card className="mb-2">{renderTeamPanel()}</Card>

            <Card>
                <TableScroll>
                    <HTMLTable striped>
                        <thead>
                            <tr>
                                <th>Member</th>
                                <th>Role</th>
                                <th>Team</th>
                                <th>Occupancy</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visibleMembers.map((member) => (
                                <tr key={member.user.id}>
                                    <td>
                                        <MemberIdentity user={member.user} />
                                    </td>
                                    <td>{getMemberRole(member.user)}</td>
                                    <td>{member.teams.map((team) => team.title).join(', ')}</td>
                                    <td>{renderOccupancy(member.user.id)}</td>
                                </tr>
                            ))}
                            {visibleMembers.length === 0 && (
                                <tr>
                                    <td colSpan={4}>
                                        <DevText muted>No active members.</DevText>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </HTMLTable>
                </TableScroll>
            </Card>
        </>
    );
};

export default MonthlyOccupancy;
